import { useEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { API } from "@/App";
import logger from "@/utils/logger";
import { toast } from "sonner";
import adminApi from "./adminApi";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Users, Pencil, Trash2, Plus } from "lucide-react";
import ImageUploader from "@/components/ImageUploader";

const emptyForm = {
  name: "", description: "", price_per_person: 0, min_guests: 10, max_guests: 100,
  package_type: "wedding", includes: "", image_url: "",
};

const PackageForm = ({ initial, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(() => {
    if (initial) {
      return { ...initial, includes: Array.isArray(initial.includes) ? initial.includes.join("\n") : (initial.includes || "") };
    }
    return emptyForm;
  });
  const update = (key, val) => setForm((p) => ({ ...p, [key]: val }));

  const handleSave = () => {
    const payload = {
      ...form,
      price_per_person: parseFloat(form.price_per_person) || 0,
      min_guests: parseInt(form.min_guests) || 10,
      max_guests: parseInt(form.max_guests) || 100,
      includes: form.includes.split("\n").map((s) => s.trim()).filter(Boolean),
    };
    onSave(payload);
  };

  const isValid = form.name && form.description && form.package_type;

  return (
    <div className="space-y-4">
      <div>
        <Label className="mb-1 block">Package Name</Label>
        <Input value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="package-name-input" />
      </div>
      <div>
        <Label className="mb-1 block">Type</Label>
        <Select value={form.package_type} onValueChange={(v) => update("package_type", v)}>
          <SelectTrigger data-testid="package-type-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            {["wedding", "corporate", "traditional", "private"].map((t) => (
              <SelectItem key={t} value={t}>{t.charAt(0).toUpperCase() + t.slice(1)}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      <div>
        <Label className="mb-1 block">Description</Label>
        <Textarea value={form.description} onChange={(e) => update("description", e.target.value)} rows={2} data-testid="package-desc-input" />
      </div>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <Label className="mb-1 block">Price/Person ($)</Label>
          <Input type="number" value={form.price_per_person} onChange={(e) => update("price_per_person", e.target.value)} data-testid="package-price-input" />
        </div>
        <div>
          <Label className="mb-1 block">Min Guests</Label>
          <Input type="number" value={form.min_guests} onChange={(e) => update("min_guests", e.target.value)} />
        </div>
        <div>
          <Label className="mb-1 block">Max Guests</Label>
          <Input type="number" value={form.max_guests} onChange={(e) => update("max_guests", e.target.value)} />
        </div>
      </div>
      <div>
        <Label className="mb-1 block">What&apos;s Included (one per line)</Label>
        <Textarea value={form.includes} onChange={(e) => update("includes", e.target.value)} rows={3} placeholder="Appetizers\nMain Course\nDessert" data-testid="package-includes-input" />
      </div>
      <div>
        <Label className="mb-1 block">Image</Label>
        <ImageUploader currentUrl={form.image_url} onUploaded={(url) => update("image_url", url)} />
      </div>
      <div className="flex gap-3 pt-2">
        <Button onClick={handleSave} disabled={saving || !isValid} data-testid="package-save-btn" className="bg-[#2D6A4F] hover:bg-[#245a42] text-white">
          {saving ? "Saving..." : "Save"}
        </Button>
        {onCancel && <Button variant="outline" onClick={onCancel}>Cancel</Button>}
      </div>
    </div>
  );
};

const PackagesManagement = () => {
  const { t } = useTranslation();
  const [packages, setPackages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [showAdd, setShowAdd] = useState(false);

  const fetchPackages = useCallback(async () => {
    try {
      const response = await adminApi.get(`${API}/packages`);
      setPackages(response.data);
    } catch (error) {
      logger.error("Error fetching packages:", error);
    } finally {
      setLoading(false);
    }
  }, [API, adminApi]);

  useEffect(() => { fetchPackages(); }, [fetchPackages]);

  const handleCreate = async (form) => {
    setSaving(true);
    try {
      await adminApi.post(`${API}/packages`, form);
      toast.success("Package added!");
      setShowAdd(false);
      fetchPackages();
    } catch { toast.error("Failed to add package"); }
    finally { setSaving(false); }
  };

  const handleUpdate = async (form) => {
    setSaving(true);
    try {
      await adminApi.put(`${API}/packages/${editingId}`, form);
      toast.success("Package updated!");
      setEditingId(null);
      fetchPackages();
    } catch { toast.error("Failed to update package"); }
    finally { setSaving(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this package?")) return;
    try {
      await adminApi.delete(`${API}/packages/${id}`);
      toast.success("Package deleted!");
      fetchPackages();
    } catch { toast.error("Failed to delete package"); }
  };

  return (
    <div data-testid="packages-management" className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-[#3D405B] font-['Playfair_Display']">{t("admin.dashboard.packages")}</h1>
          <p className="text-[#6B705C]">{t("admin.dashboard.managePackages")}</p>
        </div>
        <Dialog open={showAdd} onOpenChange={setShowAdd}>
          <DialogTrigger asChild>
            <Button className="bg-[#2D6A4F] hover:bg-[#245a42] text-white" data-testid="add-package-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Package
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>Add Package</DialogTitle></DialogHeader>
            <PackageForm onSave={handleCreate} saving={saving} />
          </DialogContent>
        </Dialog>
      </div>

      {loading ? (
        <div className="flex justify-center py-12"><div className="spinner"></div></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {packages.map((pkg) => (
            <Card key={pkg.id} className="bg-white border-0 shadow-sm" data-testid={`admin-package-${pkg.id}`}>
              <CardContent className="p-6">
                {editingId === pkg.id ? (
                  <PackageForm
                    initial={pkg}
                    onSave={handleUpdate}
                    onCancel={() => setEditingId(null)}
                    saving={saving}
                  />
                ) : (
                  <>
                    {pkg.image_url && (
                      <img src={pkg.image_url} alt={pkg.name} className="w-full h-40 object-cover rounded-lg mb-4" />
                    )}
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <Badge className="bg-[#2D6A4F]/10 text-[#2D6A4F] mb-2">{pkg.package_type}</Badge>
                        <h3 className="text-xl font-semibold text-[#3D405B]">{pkg.name}</h3>
                      </div>
                      <div className="flex gap-1">
                        <Button variant="ghost" size="sm" onClick={() => setEditingId(pkg.id)} data-testid={`edit-package-${pkg.id}`}>
                          <Pencil className="w-4 h-4 text-[#3D405B]" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDelete(pkg.id)} className="text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <p className="text-[#6B705C] text-sm mb-4">{pkg.description}</p>
                    <div className="flex items-center gap-4 text-sm text-[#6B705C]">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {pkg.min_guests}-{pkg.max_guests} {t("admin.dashboard.guests")}
                      </span>
                      <span className="font-medium text-[#E07A5F]">${pkg.price_per_person}/person</span>
                    </div>
                    {pkg.includes && pkg.includes.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {pkg.includes.map((item, i) => (
                          <Badge key={i} variant="outline" className="text-xs">{item}</Badge>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default PackagesManagement;
